import * as faceapi from "face-api.js"
import { loadFaceModels, FACE_DETECT_OPTIONS } from "./faceModels"

/**
 * Helpers photo membre : fichier téléphone / galerie → JPEG data URL (+ descriptor si visage détecté).
 */

export function loadHtmlImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error("Image invalide"))
    img.src = src
  })
}

/** Lit un fichier image et le convertit en JPEG data URL (max côté). */
export async function readImageFileAsJpegDataUrl(file, { maxSize = 900, quality = 0.9 } = {}) {
  if (!file) throw new Error("Aucun fichier")
  if (!String(file.type || "").startsWith("image/")) {
    throw new Error("Choisissez une image (JPG, PNG, WebP…)")
  }

  const dataUrl = await new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result || ""))
    reader.onerror = () => reject(new Error("Lecture du fichier impossible"))
    reader.readAsDataURL(file)
  })

  return resizeDataUrlToJpeg(dataUrl, maxSize, quality)
}

export async function resizeDataUrlToJpeg(dataUrl, maxSize = 900, quality = 0.9) {
  const img = await loadHtmlImage(dataUrl)
  const scale = Math.min(1, maxSize / Math.max(img.width, img.height || 1))
  const w = Math.max(1, Math.round(img.width * scale))
  const h = Math.max(1, Math.round(img.height * scale))
  const canvas = document.createElement("canvas")
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext("2d")
  if (!ctx) throw new Error("Canvas indisponible")
  ctx.drawImage(img, 0, 0, w, h)
  return canvas.toDataURL("image/jpeg", quality)
}

/**
 * Détecte un visage et recadre (comme la webcam). Si aucun visage → photo entière + descriptor null.
 * @returns {Promise<{ photo: string, faceDescriptor: number[]|null, faceFound: boolean }>}
 */
export async function extractFaceFromDataUrl(dataUrl) {
  await loadFaceModels()
  const img = await loadHtmlImage(dataUrl)
  const result = await faceapi
    .detectSingleFace(img, FACE_DETECT_OPTIONS)
    .withFaceLandmarks()
    .withFaceDescriptor()

  if (!result) {
    return { photo: dataUrl, faceDescriptor: null, faceFound: false }
  }

  const { x, y, width, height } = result.detection.box
  const size = Math.max(width, height)
  const canvas = document.createElement("canvas")
  canvas.width = 300
  canvas.height = 300
  const ctx = canvas.getContext("2d")
  if (!ctx) {
    return {
      photo: dataUrl,
      faceDescriptor: Array.from(result.descriptor),
      faceFound: true,
    }
  }
  ctx.drawImage(img, x, y, size, size, 0, 0, 300, 300)

  return {
    photo: canvas.toDataURL("image/jpeg", 0.9),
    faceDescriptor: Array.from(result.descriptor),
    faceFound: true,
  }
}

/**
 * Pipeline fichier → JPEG + tentative descriptor.
 * Ne bloque jamais si la détection échoue (photo cloud / terminal suffit).
 */
export async function processMemberPhotoFile(file) {
  const jpeg = await readImageFileAsJpegDataUrl(file)
  try {
    return await extractFaceFromDataUrl(jpeg)
  } catch (err) {
    console.warn("face detect on upload skipped", err)
    return { photo: jpeg, faceDescriptor: null, faceFound: false }
  }
}
